/**
 * PostHog helpers for the event booking flow.
 *
 * Initialised explicitly from the standalone production app entrypoint so
 * widget embeds and showcase/documentation bundles never activate PostHog just
 * by importing booking pages.
 *
 * Rules enforced here:
 *  - posthog.init() is called at most once via initPostHog().
 *  - Every exported function is wrapped in try/catch and is non-blocking —
 *    a tracking failure must never break the booking flow.
 */

import type posthog from "posthog-js"

let initialized = false
let client: typeof posthog | null = null
let initPromise: Promise<void> | null = null

export function initPostHog(): void {
  if (initialized || initPromise) return

  initPromise = import("posthog-js")
    .then(({ default: posthogClient }) => {
      posthogClient.init("phc_uAaj2YNKEhyvwKpVP6Dvt8aFBmyUSViGgBV5zCc5vLom", {
        api_host: "https://eu.i.posthog.com",
        defaults: "2026-05-30",
        person_profiles: "identified_only",
      })
      client = posthogClient
      initialized = true
    })
    .catch(() => {
      // non-blocking
    })
}

/**
 * Fire a PostHog custom event with optional properties.
 */
export function phCapture(
  event: string,
  properties?: Record<string, unknown>
): void {
  if (!initialized || !client) return

  try {
    client.capture(event, properties)
  } catch {
    // non-blocking
  }
}

/**
 * Identify the current user in PostHog.
 * Called once per session when the contact email is known (at booking
 * creation), before any subsequent captures.
 *
 * Uses email as the distinct_id so PostHog merges the anonymous pre-booking
 * session with the identified profile.
 */
export function phIdentify(
  email: string,
  properties: {
    first_name: string
    last_name: string
    phone: string
  }
): void {
  if (!initialized || !client) return

  try {
    client.identify(email, {
      email,
      first_name: properties.first_name,
      last_name: properties.last_name,
      phone: properties.phone,
    })
  } catch {
    // non-blocking
  }
}
